import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  emptyWorkOrdersListFilters,
  type StrapiWorkOrderRow,
  type WorkOrdersListFilters,
} from '../../../../api/strapiCrm';
import { WorkOrderDataGrid } from '../../../../components/WorkOrderDataGrid';
import { useAuth } from '../../../../auth/AuthContext';
import { isMarketingPic, isPetugasSampling, isSuperadminUser } from '../../../QuotationsPage/quotationWorkflow';
import {
  canOpenWorkOrderSchedule,
  workOrderLinkedScheduleDocumentId,
  workOrderListActions,
} from '../../workOrderListActions';
import { WorkOrderStatusMenuItemLabel, WorkOrderStatusTooltip } from '../../WorkOrderStatusHover';
import { workOrderStatusLabel, type WorkOrderStatus } from '../../workOrderShared';
import { useNavigationMenuQuery, useWorkOrdersQuery } from '../../../../query/hooks';
import { QuotationPdfPreviewDialog } from '../../../QuotationsPage/QuotationPdfPreviewDialog';
import { WorkOrderPdfPreviewDialog } from '../../WorkOrderPdfPreviewDialog';
import { LIST_FILTERS_APPLY_HINT, commitListFiltersOnEnter } from '../../../../ui/listFiltersApi';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';

const WO_FILTER_STATUS_ALL = 'ALL' as const;
const WO_FILTER_STATUS_OPTIONS = [
  WO_FILTER_STATUS_ALL,
  'draft',
  'open',
  'in_progress',
  'completed',
  'cancelled',
] as const;

type WoFilterStatus = (typeof WO_FILTER_STATUS_OPTIONS)[number];

const woFilterStatusLabel: Record<WoFilterStatus, string> = {
  [WO_FILTER_STATUS_ALL]: '(Semua status)',
  ...workOrderStatusLabel,
};

type WoFilterDraft = {
  quotationRefs: string;
  customer: string;
  workOrderDate: string;
  status: WoFilterStatus;
  workOrderDocumentId: string;
};

const initialWoDraft: WoFilterDraft = {
  quotationRefs: '',
  customer: '',
  workOrderDate: '',
  status: WO_FILTER_STATUS_ALL,
  workOrderDocumentId: '',
};

function cloneEmptyWoApplied(): WorkOrdersListFilters {
  return { ...emptyWorkOrdersListFilters };
}

function woAppliedIsActive(f: WorkOrdersListFilters): boolean {
  return (
    f.quotationRefs.trim() !== '' ||
    f.customer.trim() !== '' ||
    f.workOrderDate.trim() !== '' ||
    f.status.trim() !== '' ||
    f.workOrderDocumentId.trim() !== ''
  );
}

function draftToWoApplied(d: WoFilterDraft): WorkOrdersListFilters {
  return {
    quotationRefs: d.quotationRefs.trim(),
    customer: d.customer.trim(),
    workOrderDate: d.workOrderDate.trim(),
    status: d.status === WO_FILTER_STATUS_ALL ? '' : d.status,
    workOrderDocumentId: d.workOrderDocumentId.trim(),
  };
}

function woDraftLooksInitial(d: WoFilterDraft): boolean {
  return (
    d.quotationRefs.trim() === '' &&
    d.customer.trim() === '' &&
    d.workOrderDate.trim() === '' &&
    d.status === WO_FILTER_STATUS_ALL &&
    d.workOrderDocumentId.trim() === ''
  );
}

export function WorkOrderListTabScreen() {
  const navigate = useNavigate();
  const { jwt, user } = useAuth();
  const listActions = workOrderListActions(user);

  const menuQ = useNavigationMenuQuery(jwt);
  const showCustomPdfButton = isSuperadminUser(menuQ.data?.access_codes);

  const [draft, setDraft] = useState<WoFilterDraft>(initialWoDraft);
  const [appliedFilters, setAppliedFilters] =
    useState<WorkOrdersListFilters>(cloneEmptyWoApplied);
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const commitFilters = useCallback(() => {
    setAppliedFilters(draftToWoApplied(draftRef.current));
  }, []);

  const listFilters = useMemo<WorkOrdersListFilters>(() => {
    if (!isPetugasSampling(user)) return appliedFilters;
    const id = Number(user?.id ?? 0);
    return { ...appliedFilters, surveyTeamUserId: Number.isFinite(id) && id > 0 ? id : 0 };
  }, [appliedFilters, user]);

  const { data, isPending, isError, error, refetch, isFetching } = useWorkOrdersQuery(
    jwt,
    listFilters,
  );
  const [kupPreviewRow, setKupPreviewRow] = useState<StrapiWorkOrderRow | null>(null);
  const [workOrderPdfRow, setWorkOrderPdfRow] = useState<StrapiWorkOrderRow | null>(null);
  const [pdfRegenerateFeedback, setPdfRegenerateFeedback] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  function resetFilters(): void {
    setDraft(initialWoDraft);
    setAppliedFilters(cloneEmptyWoApplied());
  }

  const goView = useCallback(
    (row: StrapiWorkOrderRow) => {
      navigate(`/sampling/work-order/${encodeURIComponent(row.documentId)}/view`);
    },
    [navigate],
  );

  const goEdit = useCallback(
    (row: StrapiWorkOrderRow) => {
      navigate(`/sampling/work-order/${encodeURIComponent(row.documentId)}/edit`);
    },
    [navigate],
  );

  const onPreviewKupPdf = useCallback((row: StrapiWorkOrderRow) => {
    setKupPreviewRow(row);
  }, []);

  const onPreviewWorkOrderPdf = useCallback((row: StrapiWorkOrderRow) => {
    setWorkOrderPdfRow(row);
  }, []);

  const goSchedule = useCallback(
    (row: StrapiWorkOrderRow) => {
      const scheduleId = workOrderLinkedScheduleDocumentId(row);
      if (!scheduleId) return;
      navigate(`/sampling/schedules/${encodeURIComponent(scheduleId)}`);
    },
    [navigate],
  );

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {isPetugasSampling(user)
          ? 'Menampilkan work order yang Anda dijadwalkan sebagai petugas sampling.'
          : 'Work order hanya bisa dibuat dari daftar quotation (ikon kontak / assignment pada baris quotation). Setiap work order baru harus memuat minimal satu sampel.'}
      </Alert>

      <CollapsibleListFilters
        filterToggleTestId="work-orders-filter-toggle"
        actions={
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            startIcon={
              isFetching ? (
                <CircularProgress size={16} thickness={5} aria-label="Memuat ulang…" />
              ) : (
                <RefreshIcon aria-hidden />
              )
            }
            disabled={!jwt || isFetching}
            onClick={() => void refetch()}
            data-testid="work-orders-refresh-button"
          >
            Muat ulang
          </Button>
        }
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {LIST_FILTERS_APPLY_HINT}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="Quotation / job"
            value={draft.quotationRefs}
            onChange={(e) => setDraft((d) => ({ ...d, quotationRefs: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Job no., no. quotation, atau ID quotation"
            inputProps={{ 'data-testid': 'work-orders-filter-quotation' }}
          />
          <TextField
            label="Customer / perusahaan"
            value={draft.customer}
            onChange={(e) => setDraft((d) => ({ ...d, customer: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Nama customer / perusahaan atau no. pelanggan"
            inputProps={{ 'data-testid': 'work-orders-filter-customer' }}
          />
          <TextField
            label="Tanggal WO"
            value={draft.workOrderDate}
            onChange={(e) => setDraft((d) => ({ ...d, workOrderDate: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Substring tanggal ISO"
            inputProps={{ 'data-testid': 'work-orders-filter-date' }}
          />
          <WorkOrderStatusTooltip status={draft.status === WO_FILTER_STATUS_ALL ? null : draft.status}>
            <FormControl size="small" fullWidth sx={{ minWidth: 140 }}>
              <InputLabel id="work-orders-filter-status-label">Status</InputLabel>
              <Select
                labelId="work-orders-filter-status-label"
                label="Status"
                value={draft.status}
                onChange={(e) => {
                  const v = e.target.value as WoFilterStatus;
                  setDraft((d) => {
                    const next = { ...d, status: v };
                    draftRef.current = next;
                    setAppliedFilters(draftToWoApplied(next));
                    return next;
                  });
                }}
                data-testid="work-orders-filter-status"
              >
                {WO_FILTER_STATUS_OPTIONS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v === WO_FILTER_STATUS_ALL ? (
                      woFilterStatusLabel[v]
                    ) : (
                      <WorkOrderStatusMenuItemLabel status={v as WorkOrderStatus} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </WorkOrderStatusTooltip>
          <TextField
            label="ID work order"
            value={draft.workOrderDocumentId}
            onChange={(e) => setDraft((d) => ({ ...d, workOrderDocumentId: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="DocumentId (substring)"
            inputProps={{ 'data-testid': 'work-orders-filter-work-order-id' }}
          />
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {woAppliedIsActive(appliedFilters)
              ? `${rows.length} baris (total terfilter menurut server: ${total}).`
              : `Total menurut server: ${total}.`}
          </Typography>
          <Button
            size="small"
            color="inherit"
            disabled={!woAppliedIsActive(appliedFilters) && woDraftLooksInitial(draft)}
            onClick={resetFilters}
          >
            Reset filter
          </Button>
        </Stack>
      </CollapsibleListFilters>

      <WorkOrderDataGrid
        rows={rows}
        isLoading={isPending}
        isError={isError}
        error={error}
        onView={listActions.showView ? goView : undefined}
        onEdit={listActions.showEdit ? goEdit : undefined}
        onPreviewKupPdf={listActions.showKupPdf ? onPreviewKupPdf : undefined}
        onPreviewWorkOrderPdf={listActions.showWorkOrderPdf ? onPreviewWorkOrderPdf : undefined}
        onViewSchedule={canOpenWorkOrderSchedule(user) ? goSchedule : undefined}
        pdfRegenerateFeedback={pdfRegenerateFeedback}
        onClearPdfRegenerateFeedback={() => setPdfRegenerateFeedback(null)}
      />

      <WorkOrderPdfPreviewDialog
        open={workOrderPdfRow != null}
        workOrderDocumentId={workOrderPdfRow?.documentId ?? null}
        jobNumber={workOrderPdfRow?.job_number}
        companyName={workOrderPdfRow?.quotation?.customer?.name}
        jwt={jwt}
        canSign={isMarketingPic(user)}
        showHtmlPreview={showCustomPdfButton}
        onClose={() => setWorkOrderPdfRow(null)}
      />

      <QuotationPdfPreviewDialog
        open={kupPreviewRow?.quotation?.documentId != null}
        pdfKind="kup"
        documentId={kupPreviewRow?.quotation?.documentId ?? null}
        quotationNumber={kupPreviewRow?.quotation?.quotation_number}
        companyName={kupPreviewRow?.quotation?.customer?.name}
        jwt={jwt}
        showCustomPdfButton={showCustomPdfButton}
        onClose={() => setKupPreviewRow(null)}
        onRegenerateSuccess={(text) => setPdfRegenerateFeedback({ severity: 'success', text })}
        onRegenerateError={(text) => setPdfRegenerateFeedback({ severity: 'error', text })}
      />
    </Box>
  );
}
